/**
 * ============================================================================
 * PAYMENT STORE - ZUSTAND
 * ============================================================================
 * 
 * Global payment state management
 * 
 * @typedef {Object} Payment
 * @property {string} id
 */

import { create } from 'zustand';
import { validateId, validateArray, validateObject, validatePayment } from './utils/validators';
import { addItemOptimized, updateItemInArray, limitArraySize, deduplicateById } from './utils/performance';
import { createSelector } from './utils/selectors';

const usePaymentStore = create((set, get) => ({
  // State
  payments: [],
  currentPayment: null,
  paymentMethods: [],
  paymentStats: null,
  loading: false,
  error: null,
  filters: {
    status: null,
    method: null,
    dateRange: null,
  },
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },

      // Actions
      /**
       * Set payments with validation and size limit
       * @param {Payment[]} payments - Payments array
       */
      setPayments: (payments) => {
        validateArray(payments, 'Payments');
        const validated = payments.map((p) => {
          validatePayment(p);
          return p;
        });
        const deduplicated = deduplicateById(validated);
        const limited = limitArraySize(deduplicated, 1000);
        set({ payments: limited });
      },

      /**
       * Add payment with validation and deduplication
       * @param {Payment} payment - Payment object
       */
      addPayment: (payment) => {
        validatePayment(payment);
        set((state) => ({
          payments: addItemOptimized(state.payments, payment, {
            idKey: 'id',
            maxSize: 1000,
            prepend: true,
          }),
        }));
      },

      /**
       * Update payment with validation
       * @param {string} paymentId - Payment ID
       * @param {Object} updates - Updates to apply
       */
      updatePayment: (paymentId, updates) => {
        validateId(paymentId, 'Payment ID');
        validateObject(updates, 'Updates');
        set((state) => ({
          payments: updateItemInArray(state.payments, paymentId, updates),
          currentPayment:
            state.currentPayment?.id === paymentId
              ? { ...state.currentPayment, ...updates }
              : state.currentPayment,
        }));
      },

  removePayment: (paymentId) => {
    set((state) => ({
      payments: state.payments.filter((p) => p.id !== paymentId),
      currentPayment:
        state.currentPayment?.id === paymentId
          ? null
          : state.currentPayment,
    }));
  },

  setCurrentPayment: (payment) => {
    set({ currentPayment: payment });
  },

  setPaymentMethods: (methods) => {
    set({ paymentMethods: methods });
  },

  setPaymentStats: (stats) => {
    set({ paymentStats: stats });
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 },
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        status: null,
        method: null,
        dateRange: null,
      },
      pagination: { ...get().pagination, page: 1 },
    });
  },

  setPagination: (pagination) => {
    set((state) => ({
      pagination: { ...state.pagination, ...pagination },
    }));
  },

  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      payments: [],
      currentPayment: null,
      paymentMethods: [],
      paymentStats: null,
      loading: false,
      error: null,
      filters: {
        status: null,
        method: null,
        dateRange: null,
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },
    });
  },
}));

// Memoized Selectors
export const selectPaymentsByStatus = createSelector(
  (state, status) => {
    return state.payments.filter((p) => p.status === status);
  }
);

export const selectPaymentStats = createSelector(
  (state) => {
    const { payments } = state;
    return {
      total: payments.length,
      byStatus: payments.reduce((acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1;
        return acc;
      }, {}),
      totalAmount: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
    };
  }
);

export default usePaymentStore;
